import {
  type UserReviews_userProfile$data,
  type UserReviews_userProfile$key,
} from '#app/__generated__/relay/UserReviews_userProfile.graphql.js';
import { Text } from '#app/components/Primitives/Text.js';
import { buildRecommendationSentence } from '#app/features/profile/components/ProfileRecommendations/utils.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { formatName } from '#app/utilities/formatName.js';
import { EmptyState } from './EmptyState.js';
import { UserReviewCard } from './UserReviewCard/UserReviewCard.js';
import { resolveUserReviewsVisibility } from './utils.js';
import { graphql, useFragment } from '@contra/relay';
import { css, styled } from 'styled-components';

export const ContactReviewsEmptyState = styled(Text).attrs({
  textStyle: ['bodySmall', 'bodyRegular'],
})`
  ${({ theme }) => css`
    border: 1px solid ${theme.colorsV2.gray30};
    max-width: 720px;
    align-self: start;
    border-radius: 20px;
    background-color: ${theme.colorsV2.white};
    padding: 16px;
    width: 100%;
    color: ${theme.colorsV2.gray60};

    ${theme.mediaQueries.md} {
      padding: 32px;
    }
  `}
`;
type UserReviewsProps = {
  readonly connectedUserEmail?: null | string;
  readonly profileKey: UserReviews_userProfile$key;
  readonly section?: 'contact' | 'profile';
};

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 32px;

    ${theme.mediaQueries.sm} {
      gap: 40px;
    }

    & > div:last-child {
      border-bottom: none;
    }
  `}
`;

const getReviewAuthorRoleOnPaidProject = (
  userReview: UserReviews_userProfile$data['userReviews']['edges'][0]['node'],
) => {
  const { associatedWork, author } = userReview;
  if (associatedWork.paidProjectProfile) {
    return associatedWork.paidProjectProfile.contractor.id === author.id
      ? 'Freelancer'
      : 'Client';
  }

  return 'Client';
};

const getCompanyName = (
  userReview: UserReviews_userProfile$data['userReviews']['edges'][0]['node'],
  fallback: string,
) => {
  const { author } = userReview;
  return author.organization?.name ?? fallback;
};

const getReviewTextWithFallback = (
  userReview: UserReviews_userProfile$data['userReviews']['edges'][0]['node'],
  recipientFirstName: string,
) => {
  const authorFirstName = formatName(
    { firstName: userReview.author.firstName },
    'firstName',
  );

  const authorRoleOnPaidProject = getReviewAuthorRoleOnPaidProject(userReview);

  const shouldShowCompany =
    Boolean(userReview.author.organization?.name) &&
    authorRoleOnPaidProject === 'Client';

  const companyNameWithFallback = getCompanyName(userReview, 'a company');

  const reviewText = userReview.reviewText
    ? userReview.reviewText
    : buildRecommendationSentence({
        authorName: authorFirstName,
        companyName: companyNameWithFallback,
        recipientName: recipientFirstName,
        shouldShowCompany,
      });

  return reviewText;
};

const getGroupedReviews = (profile: UserReviews_userProfile$data) => {
  const { firstName, userReviews } = profile;
  const recipientFirstName = formatName({ firstName }, 'firstName');

  const positiveRecommendations =
    userReviews?.edges.map(({ node }) => {
      const reviewText = getReviewTextWithFallback(node, recipientFirstName);

      return {
        node: {
          ...node,
          hasReviewText: Boolean(node.reviewText),
          reviewText,
        },
      };
    }) ?? [];

  const reviewGroups: Map<string, typeof positiveRecommendations> = new Map();

  for (const recommendation of positiveRecommendations) {
    if (reviewGroups.has(recommendation.node.author.id)) {
      reviewGroups.get(recommendation.node.author.id)?.push(recommendation);
    } else {
      reviewGroups.set(recommendation.node.author.id, [recommendation]);
    }
  }

  return Array.from(reviewGroups.values()).toSorted((first, second) =>
    first[0].node.createdAt > second[0].node.createdAt ? -1 : 1,
  );
};

export const UserReviews = ({
  profileKey,
  section = 'profile',
}: UserReviewsProps) => {
  const profile = useFragment<UserReviews_userProfile$key>(
    graphql`
      fragment UserReviews_userProfile on UserProfile {
        id
        visitorCanEdit
        firstName
        lastName
        displayUsername
        recommendationRequests {
          count
        }
        userReviews(
          filter: { isPositiveReview: true, onlyVisibleToRecipient: false }
        ) {
          edges {
            node {
              id
              commentOnlyVisibleToRecipient
              createdAt
              onlyVisibleToRecipient
              isPositiveReview
              reviewText
              author {
                organizationV2 {
                  id
                }
                id
                firstName
                lastName
                displayUsername
                onboardingType
                avatarImage {
                  uid
                  url
                }
                organization {
                  name
                  logoImage {
                    id
                    uid
                    url
                  }
                }
              }
              associatedWork {
                paidProjectProfile {
                  id
                  contractor {
                    id
                  }
                }
                portfolioProject {
                  id
                }
                productizedService {
                  id
                }
              }
            }
          }
        }
      }
    `,
    profileKey,
  );

  const currentUser = useCurrentUser();
  const { recommendationRequests } = profile;

  const positiveRecommendations = getGroupedReviews(profile);

  const organizationRecommendations = positiveRecommendations.filter(
    ([
      {
        node: { author },
      },
    ]) =>
      author.id === currentUser?.profile.id ||
      author?.organizationV2?.id === currentUser?.profile?.organizationV2?.id,
  );

  const { shouldShowUserReviews } = resolveUserReviewsVisibility({
    hasPositiveUserReviews:
      section === 'contact'
        ? Boolean(organizationRecommendations.length)
        : Boolean(positiveRecommendations.length),
    visitorCanEdit: profile.visitorCanEdit,
  });

  const { isContractor } = useSelectedUserType();

  // don't show an empty state if the user is a contractor, OR, if the user has approved requested recommendations
  const shouldHideEmptyStateIfNoRecommendations =
    section === 'profile' && (isContractor || recommendationRequests.count > 0);

  const recommendationsToDisplay =
    section === 'contact'
      ? organizationRecommendations
      : positiveRecommendations;

  return (
    <Container>
      {shouldShowUserReviews ? (
        recommendationsToDisplay.map((recommendations) => {
          return (
            <UserReviewCard
              key={recommendations[0].node.id}
              reviews={recommendations.map(({ node }) => node)}
            />
          );
        })
      ) : shouldHideEmptyStateIfNoRecommendations ? null : (
        <>
          {section === 'contact' ? (
            <ContactReviewsEmptyState>
              Recommendations between your organization and this freelancer will
              appear here.
            </ContactReviewsEmptyState>
          ) : (
            <EmptyState />
          )}
        </>
      )}
    </Container>
  );
};
